// src/services/clientService.js (client)
import api from './api';

/**
 * Servicio para gestionar operaciones CRUD de clientes
 * Incluye filtrado automático por empresa_id del usuario autenticado
 */
export const clientService = {
  /**
   * Obtiene la lista de clientes con filtros opcionales
   * @param {Object} params - Parámetros de consulta
   * @param {string} params.orden - Campo de ordenamiento (id, business_name, created_at)
   * @param {string} params.dir - Dirección del orden (ASC, DESC)
   * @param {number} params.limit - Límite de registros
   * @param {number} params.offset - Offset para paginación
   * @param {Object} params.filtros - Filtros adicionales
   * @returns {Promise} Lista de clientes
   */
  async getClients(params = {}) {
    try {
      const queryParams = new URLSearchParams();

      // Agregar parámetros de paginación y ordenamiento
      if (params.orden) queryParams.append('orden', params.orden);
      if (params.dir) queryParams.append('dir', params.dir);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.offset) queryParams.append('offset', params.offset);

      // Agregar filtros si existen
      if (params.filtros && Object.keys(params.filtros).length > 0) {
        queryParams.append('filtros', JSON.stringify(params.filtros));
      }

      const queryString = queryParams.toString();
      const url = `/clientes${queryString ? `?${queryString}` : ''}`;

      console.log('[DEBUG Client Service] Getting clients:', url);
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('[ERROR Client Service] Error getting clients:', error);
      throw error;
    }
  },

  /**
   * Obtiene un cliente específico por ID
   * @param {number} id - ID del cliente
   * @returns {Promise} Datos del cliente
   */
  async getClient(id) {
    try {
      console.log('[DEBUG Client Service] Getting client:', id);
      const response = await api.get(`/clientes/${id}`);
      return response.data;
    } catch (error) {
      console.error('[ERROR Client Service] Error getting client:', error);
      throw error;
    }
  },

  /**
   * Crea un nuevo cliente
   * @param {Object} clientData - Datos del cliente
   * @param {string} clientData.business_name - Nombre del cliente
   * @param {string} clientData.identification_type_sri - Tipo de identificación (04=RUC, 05=Cédula)
   * @param {string} clientData.identification_number - Número de identificación
   * @param {string} clientData.email - Email (opcional)
   * @param {string} clientData.phone - Teléfono (opcional)
   * @param {string} clientData.address - Dirección (opcional)
   * @param {boolean} clientData.is_active - Estado activo (opcional, default true)
   * @returns {Promise} ID del cliente creado
   */
  async createClient(clientData) {
    try {
      console.log('[DEBUG Client Service] Creating client:', clientData);
      const response = await api.post('/clientes', clientData);
      return response.data;
    } catch (error) {
      console.error('[ERROR Client Service] Error creating client:', error);
      throw error;
    }
  },

  /**
   * Actualiza un cliente existente
   * @param {number} id - ID del cliente
   * @param {Object} clientData - Datos a actualizar
   * @returns {Promise} Confirmación de actualización
   */
  async updateClient(id, clientData) {
    try {
      console.log('[DEBUG Client Service] Updating client:', id, clientData);
      const response = await api.put(`/clientes/${id}`, clientData);
      return response.data;
    } catch (error) {
      console.error('[ERROR Client Service] Error updating client:', error);
      throw error;
    }
  },

  /**
   * Elimina (desactiva) un cliente
   * @param {number} id - ID del cliente
   * @returns {Promise} Confirmación de eliminación
   */
  async deleteClient(id) {
    try {
      console.log('[DEBUG Client Service] Deleting client:', id);
      const response = await api.delete(`/clientes/${id}`);
      return response.data;
    } catch (error) {
      console.error('[ERROR Client Service] Error deleting client:', error);
      throw error;
    }
  },

  /**
   * Valida una identificación (RUC/Cédula)
   * @param {string} identification - Número de identificación
   * @returns {Promise} Resultado de validación { valid: boolean, type: string }
   */
  async validateIdentification(identification) {
    try {
      const response = await api.get(`/validation/identification?identification=${identification}`);
      return response.data;
    } catch (error) {
      console.error('[ERROR Client Service] Error validating identification:', error);
      throw error;
    }
  }
};

export default clientService;